import classNames from 'classnames/bind';
import { Drawer } from '@mantine/core';

import { IconType } from 'types/icon';
import { WidgetType } from 'types/dashboard_widget';

import { useCurrentPlan } from 'organizations/hooks/useCurrentPlan';

import { ExternalLink } from 'components/ExternalLink/ExternalLink';
import { Heading } from 'components/Heading/Heading';
import { Icon } from 'components/Icon/IconDeprecated';
import { Text } from 'components/Text/Text';

import styles from './TypeSelect.module.scss';

const c = classNames.bind(styles);

export type AddWidgetModalTypeSelectProps = {
  onChange: (type: WidgetType) => void;
};

export function AddWidgetDrawerTypeSelect(
  props: AddWidgetModalTypeSelectProps
) {
  const { data: currentPlan } = useCurrentPlan();

  return (
    <>
      <Drawer.Header className="flex gap-2">
        <Heading level="3">Choose widget type</Heading>
        <ExternalLink href="https://docs.lumeo.com/docs/dashboard">
          Dashboard docs
        </ExternalLink>
        <Drawer.CloseButton />
      </Drawer.Header>
      <Drawer.Body>
        <div className={c('type-select')}>
          <SelectTypeButton
            {...props}
            title="Data"
            type="data"
            icon="metrics"
            info="Analyze data output from workflows."
          />
          <SelectTypeButton
            {...props}
            title="Stream"
            type="stream"
            icon="stream"
            info="Display a video player for a selected stream."
          />
          <SelectTypeButton
            {...props}
            title="Multistream"
            type="multistream"
            icon="grid"
            info="Display video players for streams matching filter criteria."
          />
          <SelectTypeButton
            {...props}
            title="Cameras"
            type="cameras"
            icon="camera"
            info="Display cameras with filters and video previews."
          />
          <SelectTypeButton
            {...props}
            title="Files"
            type="files"
            icon="file"
            info="List all files based on filter criteria."
          />
          {currentPlan?.can_search && (
            <>
              <SelectTypeButton
                {...props}
                title="Files search"
                type="files-search"
                icon="search"
                info="List files based on classification. Identify objects, events, scenes, watchlist items and text within clips and images."
              />
              <SelectTypeButton
                {...props}
                title="Watchlist status"
                type="trackable-status"
                icon="clock"
                info="See the last match time for each watchlist item in a list."
              />
            </>
          )}
          <SelectTypeButton
            {...props}
            title="Iframe"
            type="iframe"
            icon="metrics"
            info="Embed content from a web URL."
          />
          <SelectTypeButton
            {...props}
            title="Markdown"
            type="markdown"
            icon="code"
            info="Display formatted text or markdown content."
          />
          <SelectTypeButton
            {...props}
            title="System Status"
            type="system-state"
            icon="traffic-light"
            info="Display system status counts for cameras, streams, deployments, and gateways."
          />
          <SelectTypeButton
            {...props}
            title="System Events"
            type="system-events"
            icon="info"
            info="Display recent system events log."
          />
        </div>
      </Drawer.Body>
    </>
  );
}

type SelectTypeButtonProps = {
  type: WidgetType;
  title: string;
  icon: IconType;
  info: string;
  onChange: (type: WidgetType) => void;
};

function SelectTypeButton({
  title,
  type,
  icon,
  info,
  onChange,
}: SelectTypeButtonProps) {
  function handleClick() {
    onChange(type);
  }

  return (
    <button className={c('link')} onClick={handleClick}>
      <Icon className={c('icon')} name={icon} size="large" />
      <Heading className={c('heading')} level="3">
        {title}
      </Heading>
      <Text className={c('info')} align="start" type="paragraph">
        {info}
      </Text>
      <Icon className={c('chevron')} name="chevron-right" />
    </button>
  );
}
